import { useContext, useEffect, useMemo, useRef } from 'react';
import {
  ChatContextType,
  ChatCustomFilter,
  ChatFilterOperator,
  type AgentFilter,
  type CustomFilter,
} from '@trinity/chat';
import {
  ActiveChatSessionContext,
  ChatSessionFiltersContext,
} from '@trinity/chat-components/components/Chat/context';
import { FilterTypeEnum } from '@trinity/chat-components/components/Chat.Filters/interfaces';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';

type Params = {
  enabled: boolean;
  isAgentContext: boolean;
  filters: AgentFilter[];
  territoryOptions: CustomFilter.ItemValue[];
  defaultTerritoryOption: CustomFilter.ItemValue | null;
  hasLoadedTerritories: boolean;
};

type AgentFilterValueWithMetadata = CustomFilter.ItemValue & {
  filterId?: number | string;
};

type TerritorySelection = {
  id: string;
  name: string;
};

function normalizeSubjectKind(kind: string | undefined): string {
  return kind?.trim().toLowerCase() ?? '';
}

// Keeps the user-territory custom filter aligned with session context.
// Priority: territory subject (authoritative) -> user's default territory (fallback only when unset).
export function useSyncTerritoryFilterFromSubject({
  enabled,
  isAgentContext,
  filters,
  territoryOptions,
  defaultTerritoryOption,
  hasLoadedTerritories,
}: Params): void {
  const session = useContext(ActiveChatSessionContext);
  const sessionFilters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const carriedTerritoryRef = useRef<TerritorySelection | null>(null);

  const territoryFilter = useMemo(() => {
    return filters.find((filter) => filter.type === FilterTypeEnum.UserTerritory);
  }, [filters]);

  const territoryFilterIndex = useMemo(() => {
    if (!territoryFilter) {
      return -1;
    }

    return (sessionFilters.custom || []).findIndex((customFilter) => {
      if (customFilter.type !== ChatCustomFilter.AgentFilter) {
        return false;
      }

      const firstValue = customFilter.value[0] as AgentFilterValueWithMetadata | undefined;
      if (!firstValue?.filterId) {
        return false;
      }

      return Number(firstValue.filterId) === territoryFilter.id;
    });
  }, [sessionFilters.custom, territoryFilter]);

  const territorySubject = useMemo(() => {
    if (session.context.type !== ChatContextType.Agent) {
      return null;
    }

    const subject = session.context.data.subject;
    if (!subject || normalizeSubjectKind(subject.kind) !== 'territory') {
      return null;
    }

    const subjectId = String(subject.id).trim();
    if (subjectId.length === 0) {
      return null;
    }

    return {
      id: subjectId,
      name: subject.name,
    };
  }, [session.context]);

  const selectedTerritory = useMemo(() => {
    if (territoryFilterIndex === -1) {
      return null;
    }

    const existingFilter = (sessionFilters.custom ?? [])[territoryFilterIndex];
    if (existingFilter?.type !== ChatCustomFilter.AgentFilter) {
      return null;
    }

    const firstValue = existingFilter.value[0] as AgentFilterValueWithMetadata | undefined;
    if (!firstValue) {
      return null;
    }

    const selectedId = firstValue.id.trim();
    const selectedName = firstValue.name.trim();

    if (selectedId.length === 0 || selectedName.length === 0) {
      return null;
    }

    return {
      id: selectedId,
      name: selectedName,
    } satisfies TerritorySelection;
  }, [sessionFilters.custom, territoryFilterIndex]);

  useEffect(() => {
    if (!selectedTerritory) {
      return;
    }

    // Persist the latest territory selection across new-chat filter resets.
    carriedTerritoryRef.current = selectedTerritory;
  }, [selectedTerritory]);

  useEffect(() => {
    // Sync can only run after agent resources and territory options are ready.
    if (!enabled || !isAgentContext || !territoryFilter || !hasLoadedTerritories) {
      return;
    }

    const upsertTerritoryFilter = (territory: { id: string; name: string }) => {
      const territoryValue = {
        id: territory.id,
        name: territory.name,
        filterId: territoryFilter.id,
        filterLabel: territoryFilter.label,
      } as CustomFilter.ItemValue;

      const nextCustomFilter: CustomFilter.AgentCustomFilter = {
        type: ChatCustomFilter.AgentFilter,
        operator: [ChatFilterOperator.Eq],
        value: [territoryValue],
      };

      if (territoryFilterIndex === -1) {
        dispatch({
          type: 'filters/add-custom-filter',
          payload: {
            value: nextCustomFilter,
          },
        });
        return;
      }

      const existingFilter = (sessionFilters.custom ?? [])[territoryFilterIndex];
      const existingValue =
        existingFilter?.type === ChatCustomFilter.AgentFilter
          ? (existingFilter.value[0] as AgentFilterValueWithMetadata | undefined)
          : undefined;

      const alreadySynced =
        existingValue?.id === territory.id &&
        existingValue.name === territory.name &&
        Number(existingValue.filterId) === territoryFilter.id;

      if (alreadySynced) {
        return;
      }

      dispatch({
        type: 'filters/update-custom-filter',
        payload: {
          id: territoryFilterIndex,
          value: nextCustomFilter,
        },
      });
    };

    // Territory subject always wins and overwrites any existing territory filter value.
    if (territorySubject) {
      upsertTerritoryFilter(territorySubject);
      return;
    }

    if (territoryFilterIndex !== -1) {
      return;
    }

    // When starting a new chat without a territory subject, reuse the previous territory.
    const carriedTerritory = carriedTerritoryRef.current;
    if (carriedTerritory) {
      const matchingOption = territoryOptions.find((option) => option.id === carriedTerritory.id);
      upsertTerritoryFilter(matchingOption ?? carriedTerritory);
      return;
    }

    if (!defaultTerritoryOption) {
      return;
    }

    // If nothing was carried, fall back to user default only when no territory filter exists.
    upsertTerritoryFilter(defaultTerritoryOption);
  }, [
    defaultTerritoryOption,
    dispatch,
    enabled,
    hasLoadedTerritories,
    isAgentContext,
    selectedTerritory,
    sessionFilters.custom,
    territoryFilter,
    territoryFilterIndex,
    territoryOptions,
    territorySubject,
  ]);
}
